import {Country, PhonePrefix, PortalLanguage} from './types';
import {useQuery} from '@tanstack/react-query';
import {useEffect, useState} from 'react';
import isEmail from 'sane-email-validation';
import {SIGNUP_API_URL} from '~/App';

const INFINITE_CACHE_PARAMS = {
  staleTime: Infinity,
  gcTime: Infinity,
};

function useDebouncedValue<T>(value: T, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler); // Cleanup on value change or unmount
  }, [value, delay]);

  return debouncedValue;
}

export const usePredictAliasQuery = (companyName?: string) => {
  const debouncedCompanyName = useDebouncedValue(companyName, 500);
  return useQuery<string | PromiseLike<string>>({
    queryKey: ['predictAlias', debouncedCompanyName],
    queryFn: async () => {
      if (!companyName) return '';
      const response = await fetch(
        `${SIGNUP_API_URL}/signup/predictPortalAlias/${companyName}`,
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );
      return response.json();
    },
  });
};

export const useEmailAlreadyInUseQuery = (email?: string) => {
  const debouncedEmail = useDebouncedValue(email, 500);

  return useQuery<{isEmailUsed: boolean}, {status: number}>({
    queryKey: ['isEmailUsed', debouncedEmail],
    retry: false,
    queryFn: async () => {
      const response = await fetch(
        `${SIGNUP_API_URL}/common/checkEmail/${email}`
      );

      if (response.status === 409) {
        return {isEmailUsed: true};
      }

      return {isEmailUsed: false};
    },
    enabled: !!debouncedEmail && isEmail(debouncedEmail),
  });
};

export const usePhonePrefixesQuery = () => {
  return useQuery<PhonePrefix[]>({
    queryKey: ['phonePrefixes'],
    queryFn: async () => {
      const response = await fetch(`${SIGNUP_API_URL}/common/phonePrefixes`);
      return await response.json();
    },
    ...INFINITE_CACHE_PARAMS,
  });
};

export const useCountryQuery = (locale: string) => {
  return useQuery<Country[]>({
    queryKey: ['country', locale],
    queryFn: async () => {
      const response = await fetch(`${SIGNUP_API_URL}/common/countries`, {
        headers: {
          'Accept-Language': locale,
        },
      });
      return await response.json();
    },
    ...INFINITE_CACHE_PARAMS,
  });
};

export const usePortalLanguagesQuery = (locale: string) => {
  return useQuery<PortalLanguage[]>({
    queryKey: ['languages', locale],
    queryFn: async () => {
      const response = await fetch(`${SIGNUP_API_URL}/common/languages`, {
        headers: {
          'Accept-Language': locale,
        },
      });
      return await response.json();
    },
    ...INFINITE_CACHE_PARAMS,
  });
};
